import { getMinPrice } from '@shared/lib/money';
import {
	AviaPassengerType,
	CheckinPassengerFragment,
	FlightFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment as BaggageServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceMisc_Fragment as MiscServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment as VipServiceFragment,
	GdsServiceFragment,
	GdsServiceSelectorType,
	GdsServiceVipSubType,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	PassengerFragment,
	BaggageType,
	IGdsServiceInfo
} from '@websky/graphql';
import { ServicePayload } from '../Checkout/store/selectedServices/actions';
import { SelectedServicesState } from '../Checkout/store/selectedServices/reducers';
import { CartPrices } from '../Cart/types';
import {
	CardExtraGdsService,
	CardExtraFilter,
	CardExtraPrice,
	CardExtraVisibleOptions,
	SelectorType,
	ServiceType
} from './types';
import { Money as MoneyType } from '../Money/types';

import { isPetBaggage } from '../Checkout/utils';
import { getFlatArraySet, getSegmentGroupsMap, isUnconfirmedService } from '../utils';
import { ServiceSummaryType } from '../features/service-summary/model/types';

type OrderPassenger = PassengerFragment | CheckinPassengerFragment;

/**
 * Return OrderAdditionalServiceGdsServiceServiceType by ServiceType
 * @param {ServiceType} type - Extra service type
 * @returns {OrderAdditionalServiceGdsServiceServiceType}
 */
export function getGdsServiceTypeByServiceType(type: ServiceType): OrderAdditionalServiceGdsServiceServiceType {
	switch (type) {
		case 'seats':
			return OrderAdditionalServiceGdsServiceServiceType.Seat;
		case 'baggage':
		case 'baggage-delivery':
		case 'animal':
			return OrderAdditionalServiceGdsServiceServiceType.Baggage;
		case 'meal':
			return OrderAdditionalServiceGdsServiceServiceType.Meal;
		case 'business-lounge':
			return OrderAdditionalServiceGdsServiceServiceType.BusinessLounge;
		case 'priority-boarding':
		case 'capsule-hotel':
		case 'sms':
			return OrderAdditionalServiceGdsServiceServiceType.Vip;
		case 'misc':
			return OrderAdditionalServiceGdsServiceServiceType.Misc;
		default:
			return OrderAdditionalServiceGdsServiceServiceType.Other;
	}
}

export const getServiceTypeByGdsService = (service: IGdsServiceInfo): ServiceType => {
	const serviceWithSubType = service as IGdsServiceInfo & { subType?: BaggageType | GdsServiceVipSubType };

	switch (service.type) {
		case OrderAdditionalServiceGdsServiceServiceType.Seat:
			return 'seats';
		case OrderAdditionalServiceGdsServiceServiceType.Baggage:
			return 'baggage';
		case OrderAdditionalServiceGdsServiceServiceType.Meal:
			return 'meal';
		case OrderAdditionalServiceGdsServiceServiceType.BusinessLounge:
			return 'business-lounge';
		case OrderAdditionalServiceGdsServiceServiceType.Vip:
			switch (serviceWithSubType.subType) {
				case 'BaggageDelivery':
					return 'baggage-delivery';
				case 'BusinessLounge':
				case 'BusinessLoungeInternational':
					return 'business-lounge';
				case 'PriorityBoarding':
					return 'priority-boarding';
				case 'CapsuleHotel':
					return 'capsule-hotel';
				case 'GroundTransportation':
					return 'transfer';
				case 'RefundableTicket':
					return 'refundable-ticket';
				case 'Upgrade':
					return 'upgrade';
				case 'SMS':
					return 'sms';
				default:
					return 'misc';
			}
		case OrderAdditionalServiceGdsServiceServiceType.Misc:
		default:
			return 'misc';
	}
};

/**
 * Return GdsServiceVipSubType by ServiceType
 * @param {ServiceType} type - Extra service type
 * @returns {GdsServiceVipSubType}
 */
export function getGdsSubTypeByServiceType(type: ServiceType): GdsServiceVipSubType[] {
	switch (type) {
		case 'baggage-delivery':
			return [GdsServiceVipSubType.BaggageDelivery];
		case 'business-lounge':
			return [GdsServiceVipSubType.BusinessLounge, GdsServiceVipSubType.BusinessLoungeInternational];
		case 'priority-boarding':
			return [GdsServiceVipSubType.PriorityBoarding];
		case 'refundable-ticket':
			return [GdsServiceVipSubType.RefundableTicket];
		case 'upgrade':
			return [GdsServiceVipSubType.Upgrade];
		case 'capsule-hotel':
			return [GdsServiceVipSubType.CapsuleHotel];
		case 'sms':
			return [GdsServiceVipSubType.SMS];
		default:
			return [GdsServiceVipSubType.UnknownSubtype];
	}
}

/**
 * Return cart prices key by service type
 * @param {ServiceType} type - Extra service type
 * @returns {string}
 */
export function getCartPriceKeyByType(type: ServiceType): keyof CartPrices {
	switch (type) {
		case 'seats':
			return 'seats';
		case 'baggage':
			return 'baggage';
		case 'baggage-delivery':
			return 'baggageDelivery';
		case 'animal':
			return 'animals';
		case 'meal':
			return 'meals';
		case 'business-lounge':
			return 'businessLounge';
		case 'priority-boarding':
			return 'priorityBoarding';
		case 'capsule-hotel':
		case 'refundable-ticket':
			return 'vip';
		case 'insurance':
			return 'insurance';
		case 'transfer':
			return 'aeroexpress';
		case 'sms':
			return 'sms';
		case 'upgrade':
			return 'upgrade';
		case 'loyalty':
		case 'misc':
			return null;
	}
}

export function getServiceSummaryType(type: ServiceType): ServiceSummaryType {
	switch (type) {
		case 'seats':
			return 'Seat';
		case 'baggage':
			return 'Baggage';
		case 'baggage-delivery':
			return 'BaggageDelivery';
		case 'animal':
			return 'Animal';
		case 'meal':
			return 'Meal';
		case 'business-lounge':
			return 'BusinessLounge';
		case 'priority-boarding':
			return 'PriorityBoarding';
		case 'refundable-ticket':
			return 'RefundableTicket';
		case 'insurance':
			return 'Insurance';
		case 'transfer':
			return 'Aeroexpress';
		case 'loyalty':
			return 'Loyalty';
		case 'capsule-hotel':
			return 'CapsuleHotel';
		case 'upgrade':
			return 'Upgrade';
		case 'sms':
			return 'Sms';
		case 'misc':
			return 'Misc';
	}
}

/**
 * Return filter additional services by service type
 *
 * @param {ServiceType} type - Extra service type
 * @param {FlightFragment} flight - Order flight
 * @param {OrderAdditionalServicesFragment} additionalServices - Order additional services
 * @param {string[]} availableProgramsCodes - Insurances available codes
 * @param {boolean} animalsAvailable - Animals service availability
 * @param {CardExtraFilter} filter - Filter object
 *
 * @returns {OrderAdditionalServicesFragment}
 */
export function filterGdsServicesByType(
	type: ServiceType,
	flight: FlightFragment,
	additionalServices: OrderAdditionalServicesFragment,
	availableProgramsCodes?: string[],
	animalsAvailable?: boolean,
	filter?: CardExtraFilter
): OrderAdditionalServicesFragment {
	// Insurance services by type
	if (type === 'insurance') {
		if (!additionalServices?.insurances?.insurancePrograms?.length) {
			return additionalServices;
		}

		return {
			...additionalServices,
			insurances: {
				...additionalServices.insurances,
				insurancePrograms: additionalServices.insurances.insurancePrograms.filter(insurance => {
					if (!availableProgramsCodes?.length) {
						return true;
					}

					return availableProgramsCodes.includes(insurance.code);
				})
			}
		};
	}

	// Gds services by type
	if (!additionalServices?.gdsServices?.services?.length) {
		return additionalServices;
	}

	if (type === 'baggage-delivery') {
		return {
			...additionalServices,
			gdsServices: {
				...additionalServices.gdsServices,
				services: additionalServices.gdsServices.services
					.filter(service => (service as VipServiceFragment).subType === GdsServiceVipSubType.BaggageDelivery)
					.reduce<GdsServiceFragment[]>((services, service) => {
						const vipService = service as VipServiceFragment;

						const segmentsMap = getSegmentGroupsMap(flight.segmentGroups);
						const allowedSegmentsSet = getFlatArraySet(service.allowedSegments);

						const newServices: GdsServiceFragment[] = [];

						allowedSegmentsSet.forEach(segmentId => {
							// WEBSKY-2899: сегмент должен быть без пересадки
							if (segmentsMap.get(segmentId)?.length === 1) {
								newServices.push({ ...vipService, allowedSegments: [[segmentId]] });
							}
						});

						return [...services, ...newServices];
					}, [])
			}
		};
	}

	return {
		...additionalServices,
		gdsServices: {
			...additionalServices.gdsServices,
			services: additionalServices.gdsServices.services.filter(service => {
				const isFiltered = !filter || filter.values.includes(service[filter.param]);

				switch (service.type) {
					// Vip services
					case OrderAdditionalServiceGdsServiceServiceType.Vip:
					case OrderAdditionalServiceGdsServiceServiceType.BusinessLounge:
						const vipService = service as VipServiceFragment;

						if (vipService.subType && !getGdsSubTypeByServiceType(type).includes(vipService.subType)) {
							return false;
						}

						return isFiltered;
					// Gds services
					default:
						if (service.type !== getGdsServiceTypeByServiceType(type)) {
							return false;
						}

						switch (type) {
							case 'baggage':
							case 'animal':
								const baggageService = service as BaggageServiceFragment;
								const isPetBaggageType = isPetBaggage(baggageService);

								return (
									(type === 'baggage'
										? !animalsAvailable || (animalsAvailable && !isPetBaggageType)
										: isPetBaggageType) && isFiltered
								);
							case 'misc':
								const miscService = service as MiscServiceFragment;

								if (filter?.param === 'subType') {
									const miscServiceSubType = miscService.subtype?.title;

									return filter.values.includes(miscServiceSubType);
								}

								return isFiltered;
							default:
								return isFiltered;
						}
				}
			})
		}
	};
}

/**
 * Return prices by passenger
 * @param {ServiceType} type - Extra service type
 * @param {OrderAdditionalServicesFragment} additionalServices - Order additional services
 * @param {OrderPassenger[]} travellers - Travellers
 * @param {boolean} minPrice - Find minimal prices
 * @returns ${CardExtraPrice[]}
 */
export function getPassengerPrices(
	type: ServiceType,
	additionalServices: OrderAdditionalServicesFragment,
	travellers: OrderPassenger[],
	minPrice?: boolean
): CardExtraPrice[] {
	const pricesMap = new Map<AviaPassengerType, CardExtraPrice>();

	if (type === 'insurance' || type === 'transfer') {
		const servicesPrices = getServicesPrices(type, additionalServices);

		if (servicesPrices.length) {
			travellers.forEach(traveller => {
				const type = traveller.type as AviaPassengerType;

				if (type !== AviaPassengerType.INF) {
					pricesMap.set(type, {
						type,
						supplierType: getPassengerSupplierType(traveller),
						price: minPrice ? getMinPrice(servicesPrices, price => price) : servicesPrices[0]
					});
				}
			});
		}
	} else {
		additionalServices.gdsServices?.services?.forEach(service => {
			const allowedPassengerIds = new Set(service.allowedPassengers.reduce((acc, ids) => [...acc, ...ids], []));

			allowedPassengerIds.forEach(passengerId => {
				const traveller = travellers.find(traveller => traveller.id === passengerId);

				if (traveller) {
					const type = traveller.type as AviaPassengerType;
					const supplierType = getPassengerSupplierType(traveller);

					const existPrice = pricesMap.get(type);
					const isLessPrice = minPrice && existPrice && existPrice.price.amount > service.price.amount;

					if (!existPrice || isLessPrice) {
						pricesMap.set(type, { type, supplierType, price: service.price });
					}
				}
			});
		});
	}

	return [...pricesMap.values()];
}

/**
 * Return can modify service value
 * @param {GdsServiceFragment} service - Gds service
 * @param {OrderPassenger} travellers - Travellers
 * @returns {boolean}
 */
export function canBeModifyService(service: GdsServiceFragment, travellers: OrderPassenger[]): boolean {
	const allowedPassengersSet = new Set(service.allowedPassengers?.reduce((acc, ids) => [...acc, ...ids], []) ?? []);

	if (!allowedPassengersSet.size) {
		return service.canBeAdded;
	}

	const travellersMap = new Map(travellers.map(traveller => [traveller.id, traveller]));

	return [...allowedPassengersSet].some(passengerId => {
		const passenger = travellersMap.get(passengerId);

		if (!passenger?.services) {
			return service.canBeAdded;
		}

		const gdsService = passenger.services?.gdsServices?.services?.find(
			selectedService => selectedService.serviceId === service.id
		);

		if (gdsService) {
			return gdsService.count > gdsService.confirmedCount;
		}

		const brandIncludedService = passenger.services?.brandIncludedServices?.services?.find(
			includedService => includedService.serviceId === service.id
		);

		if (brandIncludedService) {
			return brandIncludedService.count > brandIncludedService.confirmedCount;
		}

		return service.canBeAdded;
	});
}

/**
 * Return service visible value
 * @param {ServiceType} type - Extra service type
 * @param {FlightFragment} flight - Order flight
 * @param {OrderPassenger} travellers - Order travellers
 * @param {OrderAdditionalServicesFragment} additionalServices - Additional services
 * @param {boolean} canModifyService - Modify services availability
 * @param {CardExtraVisibleOptions} options - Visible options
 * @param {boolean} ffpSupported - Ffp supported
 * @returns {boolean}
 */
export function getServiceVisible(
	type: ServiceType,
	flight: FlightFragment,
	travellers: OrderPassenger[] = [],
	additionalServices: OrderAdditionalServicesFragment,
	canModifyService?: boolean,
	ffpSupported?: boolean,
	options?: CardExtraVisibleOptions
): boolean {
	const gdsServices = additionalServices.gdsServices?.services ?? [];
	const hasGdsServices = gdsServices.length > 0;

	switch (type) {
		case 'seats':
			const segments = flight.seatMap?.segments ?? [];

			if (travellers.some(traveller => traveller.services?.seats?.length)) {
				return true;
			}

			if (!segments.length) {
				return false;
			}

			let hasAvailableSeats = false;
			let allCheckinOnly = true;

			for (const segment of segments) {
				if (segment.availableSeatsCount > 0) {
					hasAvailableSeats = true;
				}

				if (!segment.isSeatsAvailableInCheckinOnly) {
					allCheckinOnly = false;
				}

				if (hasAvailableSeats && !allCheckinOnly) {
					return true;
				}
			}

			return hasAvailableSeats || allCheckinOnly;
		case 'baggage':
			const hasTravellersIncludedBaggage = travellers.some(traveller =>
				traveller.services?.brandIncludedServices?.services?.some(
					service =>
						service.service?.type === OrderAdditionalServiceGdsServiceServiceType.Baggage &&
						service.count > 0
				)
			);

			return canModifyService || options?.includedBaggageAvailable || hasTravellersIncludedBaggage;
		case 'animal':
			return canModifyService && options?.animalAvailable;
		case 'insurance':
			return additionalServices.insurances?.insurancePrograms?.length > 0;
		case 'transfer':
			return additionalServices.transfer?.aeroexpress?.trips?.length > 0;
		case 'loyalty':
			return ffpSupported;
		case 'refundable-ticket':
			return gdsServices.some(
				service => (service as VipServiceFragment).subType === GdsServiceVipSubType.RefundableTicket
			);
		case 'misc': {
			const hasSelectedMisc = travellers.some(traveller =>
				traveller.services?.gdsServices?.services?.some(
					selectedService =>
						selectedService.count > 0 &&
						gdsServices.some(service => service.id === selectedService.serviceId)
				)
			);

			return hasGdsServices && (!!canModifyService || hasSelectedMisc);
		}
		default:
			return canModifyService && hasGdsServices;
	}
}

/**
 * Return services prices by service type
 * @param {ServiceType} type - Extra service type
 * @param {OrderAdditionalServicesFragment} additionalServices - Additional services
 * @returns {MoneyType[]}
 */
export function getServicesPrices(type: ServiceType, additionalServices: OrderAdditionalServicesFragment): MoneyType[] {
	if (type === 'insurance') {
		return additionalServices.insurances?.insurancePrograms?.map(program => program.price) ?? [];
	} else if (type === 'transfer') {
		return additionalServices.transfer?.aeroexpress?.trips?.map(trip => trip.price) ?? [];
	}

	return additionalServices.gdsServices?.services?.map(service => service.price) ?? [];
}

/**
 * Return unconfirmed services
 * @param {OrderAdditionalServiceGdsServiceServiceType} gdsServiceType - Gds service type
 * @param {PassengerFragment} travellers - Travellers
 * @param {SelectedServicesState} selectedServices - Selected services state
 * @returns {ServicePayload[]}
 */
export function getUnconfirmedServices(
	gdsServiceType: OrderAdditionalServiceGdsServiceServiceType,
	travellers: OrderPassenger[],
	selectedServices: SelectedServicesState
): ServicePayload[] {
	const services: ServicePayload[] = [];

	if (Object.keys(selectedServices ?? {})?.length) {
		for (const passengerId in selectedServices) {
			for (const serviceId in selectedServices[passengerId]) {
				const traveller = travellers.find(traveller => traveller.id === passengerId);

				if (!traveller) {
					continue;
				}

				const service = traveller?.services?.gdsServices?.services?.find(
					service => service.serviceId === serviceId
				);

				if (!service) {
					continue;
				}

				const isUnconfirmed = service.products?.some(product => isUnconfirmedService(product.status));

				if (isUnconfirmed) {
					services.push({
						passengerId,
						serviceId: service.serviceId,
						segmentId: service.segmentIds[0],
						service: {
							id: service.serviceId,
							type: gdsServiceType
						}
					});
				}
			}
		}
	}

	return services;
}

/**
 * Service payload adapter
 * @param {CardExtraGdsService} service - Card extra gds service
 * @param {OrderAdditionalServiceGdsServiceServiceType} serviceGdsType - Gds service type
 * @param {number} count - Services count
 * @return {ServicePayload}
 */
export function extraGdsServiceToServicePayload(
	service: CardExtraGdsService,
	serviceGdsType: OrderAdditionalServiceGdsServiceServiceType,
	count?: number
): ServicePayload {
	return {
		count: count ?? service.count,
		serviceId: service.serviceId,
		passengerId: service.passengerId,
		segmentId: service.segmentIds[0],
		service: { id: service.serviceId, type: serviceGdsType }
	};
}

/**
 * @param {ServiceType} type - Extra service type
 * @param {OrderAdditionalServicesFragment} additionalServices - Additional services
 */
export function isSingleService(type: ServiceType, additionalServices: OrderAdditionalServicesFragment): boolean {
	if (type === 'insurance') {
		return additionalServices.insurances?.insurancePrograms?.length === 1;
	} else if (type === 'transfer') {
		return additionalServices.transfer?.aeroexpress?.trips?.length === 1;
	} else if (type === 'misc') {
		return additionalServices.gdsServices?.services?.every(
			service => !(service as MiscServiceFragment)?.isPopupRequired
		);
	}

	return false;
}

/**
 * Checkout passenger guard
 * @param {OrderPassenger} passenger - Checkout or checkin passenger
 * @return {boolean}
 */
export function isCheckoutPassenger(passenger: OrderPassenger): passenger is PassengerFragment {
	return (passenger as PassengerFragment)?.supplierTravellerType !== undefined;
}

export function getPassengerSupplierType(passenger: OrderPassenger): string {
	return isCheckoutPassenger(passenger) ? passenger.supplierTravellerType : undefined;
}

export function convertMiscSelectorType(type: GdsServiceSelectorType): SelectorType {
	if (!type) {
		return 'checkbox';
	}

	return ({
		[GdsServiceSelectorType.Radio]: 'switch',
		[GdsServiceSelectorType.Checkbox]: 'checkbox',
		[GdsServiceSelectorType.Counter]: 'counter'
	} as Record<GdsServiceSelectorType, SelectorType>)[type];
}
